import { resolveApiBaseUrl } from './apiBaseUrl';

const API_BASE_URL = resolveApiBaseUrl();

export const AUTH_REFRESH_URL =
  process.env.REACT_APP_AUTH_REFRESH_URL || `${API_BASE_URL}/api/auth/refresh`;
export const AUTH_LOGOUT_URL =
  process.env.REACT_APP_AUTH_LOGOUT_URL || `${API_BASE_URL}/api/auth/logout`;

export const AUTH_STORAGE_KEYS = Object.freeze({
  user: 'user',
  authToken: 'authToken',
  isAuthenticated: 'isAuthenticated',
});

export const AUTH_EVENT_TYPES = Object.freeze({
  logout: 'logout',
  login: 'login',
  sessionRefreshed: 'session_refreshed',
});

const AUTH_EVENT_STORAGE_KEY = 'liwaza_auth_event';
const AUTH_CHANNEL_NAME = 'liwaza_auth_channel';
const AUTH_SHARED_STATE_STORAGE_KEY = 'liwaza_auth_state';
const AUTH_ACTIVE_TABS_STORAGE_KEY = 'liwaza_auth_active_tabs';
const AUTH_TAB_ID_STORAGE_KEY = 'liwaza_auth_tab_id';
const AUTH_PENDING_LAST_TAB_CLOSE_STORAGE_KEY = 'liwaza_auth_pending_last_tab_close';
const AUTH_REFRESH_LOCK_STORAGE_KEY = 'liwaza_auth_refresh_lock';
const AUTH_TAB_HEARTBEAT_MS = 15000;
const AUTH_TAB_STALE_AFTER_MS = 45000;
const AUTH_REFRESH_LOCK_TTL_MS = 15000;
const AUTH_SESSION_KEEPALIVE_MS = 30 * 60 * 1000;
const AUTH_SESSION_CACHE_KEYS = [
  'gre_vocab_enrollment_session',
  'gre_vocab_enrollment_session_timestamp',
  'gre_vocab_daily_articles_session',
  'gre_vocab_daily_articles_session_timestamp',
  'gre_vocab_cache_timestamp',
  'quantPracticeSession',
  'quantPracticeReview',
  'tage_initial_lessons',
  'fromPricing',
];

let refreshPromise = null;

const createTabId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export const getAuthTabId = () => {
  if (typeof window === 'undefined') {
    return 'server';
  }

  let tabId = sessionStorage.getItem(AUTH_TAB_ID_STORAGE_KEY);
  if (tabId) {
    return tabId;
  }

  tabId = createTabId();
  sessionStorage.setItem(AUTH_TAB_ID_STORAGE_KEY, tabId);
  return tabId;
};

const getCurrentAuthEventUser = (payload = {}) => payload.user || getStoredUser();

const getSharedAuthState = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return readJsonStorageValue(localStorage, AUTH_SHARED_STATE_STORAGE_KEY);
};

const writeSharedAuthState = (nextState) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    localStorage.setItem(
      AUTH_SHARED_STATE_STORAGE_KEY,
      JSON.stringify(nextState)
    );
    return nextState;
  } catch (error) {
    console.error('Error writing shared auth state:', error);
    return null;
  }
};

const markSharedAuthenticated = (user, extra = {}) => {
  if (!user?.email) {
    return null;
  }

  return writeSharedAuthState({
    status: 'authenticated',
    userEmail: user.email,
    lastAuthenticatedAt: Date.now(),
    sessionVersion: Date.now(),
    ...extra,
  });
};

const markSharedLoggedOut = (reason = 'logout') =>
  writeSharedAuthState({
    status: reason === 'session_expired' ? 'expired' : 'logged_out',
    userEmail: null,
    lastAuthenticatedAt: null,
    sessionVersion: Date.now(),
    reason,
  });

const pruneActiveTabs = (activeTabs = {}) => {
  const now = Date.now();

  return Object.entries(activeTabs).reduce((nextTabs, [tabId, lastSeenAt]) => {
    if (now - Number(lastSeenAt || 0) <= AUTH_TAB_STALE_AFTER_MS) {
      nextTabs[tabId] = lastSeenAt;
    }
    return nextTabs;
  }, {});
};

const readActiveTabs = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  return pruneActiveTabs(
    readJsonStorageValue(localStorage, AUTH_ACTIVE_TABS_STORAGE_KEY) || {}
  );
};

const writeActiveTabs = (activeTabs) => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    localStorage.setItem(
      AUTH_ACTIVE_TABS_STORAGE_KEY,
      JSON.stringify(pruneActiveTabs(activeTabs))
    );
  } catch (error) {
    console.error('Error writing active auth tabs:', error);
  }

  return pruneActiveTabs(activeTabs);
};

const readPendingLastTabClose = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return readJsonStorageValue(localStorage, AUTH_PENDING_LAST_TAB_CLOSE_STORAGE_KEY);
};

const writePendingLastTabClose = (pendingClose) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    if (!pendingClose) {
      localStorage.removeItem(AUTH_PENDING_LAST_TAB_CLOSE_STORAGE_KEY);
      return null;
    }

    localStorage.setItem(
      AUTH_PENDING_LAST_TAB_CLOSE_STORAGE_KEY,
      JSON.stringify(pendingClose)
    );
    return pendingClose;
  } catch (error) {
    console.error('Error writing pending last-tab close state:', error);
    return null;
  }
};

const cancelPendingLastTabCloseForTab = (tabId) => {
  const pendingClose = readPendingLastTabClose();
  if (pendingClose?.tabId === tabId) {
    writePendingLastTabClose(null);
  }
};

const readRefreshLock = () =>
  readJsonStorageValue(localStorage, AUTH_REFRESH_LOCK_STORAGE_KEY);

const acquireRefreshLock = (tabId) => {
  const now = Date.now();
  const currentLock = readRefreshLock();

  if (
    currentLock &&
    currentLock.tabId !== tabId &&
    now - Number(currentLock.timestamp || 0) < AUTH_REFRESH_LOCK_TTL_MS
  ) {
    return false;
  }

  try {
    localStorage.setItem(
      AUTH_REFRESH_LOCK_STORAGE_KEY,
      JSON.stringify({ tabId, timestamp: now })
    );
    return true;
  } catch (error) {
    console.error('Error acquiring auth refresh lock:', error);
    return true;
  }
};

const releaseRefreshLock = (tabId) => {
  const currentLock = readRefreshLock();
  if (currentLock?.tabId === tabId) {
    localStorage.removeItem(AUTH_REFRESH_LOCK_STORAGE_KEY);
  }
};

const waitForOtherTabRefresh = async (tabId) => {
  const startedAt = Date.now();

  while (Date.now() - startedAt < AUTH_REFRESH_LOCK_TTL_MS) {
    const currentLock = readRefreshLock();
    if (
      !currentLock ||
      currentLock.tabId === tabId ||
      Date.now() - Number(currentLock.timestamp || 0) >= AUTH_REFRESH_LOCK_TTL_MS
    ) {
      return;
    }

    await new Promise((resolve) => {
      window.setTimeout(resolve, 100);
    });
  }
};

const shouldAttemptSessionRefresh = () => {
  // Always try if we have any in-tab session signal.
  if (getStoredUser() || getStoredAccessToken()) {
    return true;
  }

  const sharedAuthState = getSharedAuthState();
  if (sharedAuthState?.status === 'authenticated') {
    return true;
  }

  // Even if shared state says logged_out/expired, the HttpOnly refresh
  // cookie may still be valid (e.g. the user just clicked an emailed deep
  // link in a fresh tab after a previous logout-redirect). Try whenever we
  // see persisted hints of a prior login on this device.
  return Boolean(
    localStorage.getItem('userEmail') ||
      localStorage.getItem('user') ||
      localStorage.getItem('pending_subscription') ||
      sharedAuthState?.userEmail ||
      sharedAuthState?.lastAuthenticatedAt
  );
};

const readJsonStorageValue = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error(`Error parsing stored value for ${key}:`, error);
    return null;
  }
};

const normalizeAuthPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const user = payload.user || payload.profile || payload.currentUser || null;
  const accessToken =
    payload.access_token ||
    payload.accessToken ||
    payload.token ||
    payload.jwt ||
    null;

  if (!user) {
    return null;
  }

  return { user, accessToken };
};

const createAuthEvent = (type, payload = {}) => ({
  type,
  payload,
  timestamp: Date.now(),
  sourceTabId: getAuthTabId(),
});

export const getStoredUser = () =>
  readJsonStorageValue(sessionStorage, AUTH_STORAGE_KEYS.user);

export const getStoredAccessToken = () =>
  sessionStorage.getItem(AUTH_STORAGE_KEYS.authToken);

const LIWAZA_ACCESS_TOKEN_COOKIE_NAME = 'liwaza_access_token';

const readAccessTokenFromCookie = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  const segment = `; ${document.cookie}`.split(
    `; ${LIWAZA_ACCESS_TOKEN_COOKIE_NAME}=`
  );
  if (segment.length < 2) {
    return null;
  }

  const raw = segment.pop().split(';').shift();
  if (!raw) {
    return null;
  }

  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const isJwtNotExpired = (token) => {
  if (!token) {
    return false;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true;
    }

    const payload = JSON.parse(atob(parts[1]));
    const now = Date.now() / 1000;

    if (payload.exp && payload.exp < now) {
      return false;
    }

    return true;
  } catch {
    return true;
  }
};

/**
 * Token to attach as Authorization on API calls. Prefers a non-expired
 * sessionStorage JWT, then a non-expired liwaza_access_token cookie, so a
 * rotated cookie is not overridden by a stale session copy.
 */
export const getAccessTokenForApiHeaders = () => {
  const fromSession = sessionStorage.getItem(AUTH_STORAGE_KEYS.authToken);
  const fromCookie = readAccessTokenFromCookie();

  if (fromSession && isJwtNotExpired(fromSession)) {
    return fromSession;
  }
  if (fromCookie && isJwtNotExpired(fromCookie)) {
    return fromCookie;
  }

  return fromSession || fromCookie || null;
};

export const hasAuthenticatedSession = () =>
  Boolean(getStoredUser()) &&
  sessionStorage.getItem(AUTH_STORAGE_KEYS.isAuthenticated) === 'true';

const buildAuthenticatedWebSocketUrl = (url) => {
  const token = getAccessTokenForApiHeaders();
  if (!token) {
    return url;
  }

  try {
    const nextUrl = new URL(url);
    if (!nextUrl.searchParams.has('access_token')) {
      nextUrl.searchParams.set('access_token', token);
    }
    return nextUrl.toString();
  } catch {
    return url;
  }
};

export const createAuthenticatedWebSocket = (url) => {
  if (!hasAuthenticatedSession()) {
    return null;
  }

  return new WebSocket(buildAuthenticatedWebSocketUrl(url));
};

export const getStoredAuthState = () => ({
  user: getStoredUser(),
  authToken: getStoredAccessToken(),
  isAuthenticated:
    sessionStorage.getItem(AUTH_STORAGE_KEYS.isAuthenticated) === 'true',
});

export const setAuthenticatedSession = (user, accessToken) => {
  if (!user) return;

  sessionStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user));
  if (accessToken) {
    sessionStorage.setItem(AUTH_STORAGE_KEYS.authToken, accessToken);
  } else {
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.authToken);
  }
  sessionStorage.setItem(AUTH_STORAGE_KEYS.isAuthenticated, 'true');
  markSharedAuthenticated(user);
};

export const clearAuthenticatedSession = ({ preserveTabState = false } = {}) => {
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.user);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.authToken);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.isAuthenticated);

  if (!preserveTabState) {
    AUTH_SESSION_CACHE_KEYS.forEach((key) => sessionStorage.removeItem(key));
  }
};

export const isStoredAccessTokenValid = () => {
  const accessToken = getStoredAccessToken();
  if (!accessToken) return false;

  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1]));
    const now = Date.now() / 1000;

    if (payload.exp && payload.exp < now) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Invalid token format:', error);
    return false;
  }
};

const getValidStoredSession = () => {
  const currentUser = getStoredUser();
  if (currentUser && isStoredAccessTokenValid()) {
    return { ok: true, refreshed: false, user: currentUser };
  }
  return null;
};

export const refreshSessionIfNeeded = async ({
  force = false,
  broadcastOnSuccess = false,
} = {}) => {
  if (!force) {
    const validSession = getValidStoredSession();
    if (validSession) {
      return validSession;
    }

    if (!shouldAttemptSessionRefresh()) {
      return { ok: false, refreshed: false, skipped: true };
    }
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const tabId = getAuthTabId();

    try {
      await waitForOtherTabRefresh(tabId);

      if (!force) {
        const validSession = getValidStoredSession();
        if (validSession) {
          return validSession;
        }
      }

      while (!acquireRefreshLock(tabId)) {
        await waitForOtherTabRefresh(tabId);
        if (!force) {
          const validSession = getValidStoredSession();
          if (validSession) {
            return validSession;
          }
        }
      }

      const response = await fetch(AUTH_REFRESH_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        clearAuthenticatedSession({ preserveTabState: true });
        if (response.status === 401) {
          markSharedLoggedOut('refresh_failed');
        }
        return { ok: false, refreshed: false, status: response.status };
      }

      const data = await response.json().catch(() => ({}));
      const normalized = normalizeAuthPayload(data);

      if (!normalized) {
        clearAuthenticatedSession({ preserveTabState: true });
        markSharedLoggedOut('refresh_invalid_payload');
        return { ok: false, refreshed: false, status: response.status };
      }

      setAuthenticatedSession(normalized.user, normalized.accessToken);
      if (normalized.user?.email && !localStorage.getItem('userEmail')) {
        localStorage.setItem('userEmail', normalized.user.email);
      }
      if (broadcastOnSuccess) {
        publishAuthEvent(AUTH_EVENT_TYPES.sessionRefreshed, {
          user: normalized.user,
          userEmail: normalized.user?.email || null,
        });
      }
      return {
        ok: true,
        refreshed: true,
        user: normalized.user,
        authToken: normalized.accessToken || null,
      };
    } catch (error) {
      console.error('Error refreshing auth session:', error);
      clearAuthenticatedSession({ preserveTabState: true });
      return { ok: false, refreshed: false, error };
    } finally {
      releaseRefreshLock(tabId);
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const requestBackendLogout = async ({
  keepalive = false,
  fireAndForget = false,
} = {}) => {
  const request = fetch(AUTH_LOGOUT_URL, {
    method: 'POST',
    credentials: 'include',
    keepalive,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (fireAndForget) {
    request.catch((error) => {
      console.error('Error logging out from backend:', error);
    });
    return;
  }

  try {
    await request;
  } catch (error) {
    console.error('Error logging out from backend:', error);
  }
};

export const resolveAuthTabLifecycleOnBoot = () => {
  if (typeof window === 'undefined') {
    return { browserClosed: false };
  }

  const tabId = getAuthTabId();
  const pendingLastTabClose = readPendingLastTabClose();

  if (!pendingLastTabClose?.tabId) {
    return { browserClosed: false };
  }

  if (pendingLastTabClose.tabId === tabId) {
    writePendingLastTabClose(null);
    return { browserClosed: false };
  }

  if (getSharedAuthState()?.status === 'authenticated') {
    markSharedLoggedOut('browser_closed');
    requestBackendLogout({
      keepalive: true,
      fireAndForget: true,
    });
  }

  writePendingLastTabClose(null);
  return { browserClosed: true };
};

export const bootstrapAuthSession = async () => refreshSessionIfNeeded();

export const registerAuthTabLifecycle = () => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const tabId = getAuthTabId();
  let didUnregister = false;

  const touchTab = () => {
    const activeTabs = readActiveTabs();
    activeTabs[tabId] = Date.now();
    writeActiveTabs(activeTabs);
  };

  const unregisterTab = () => {
    if (didUnregister) {
      return;
    }
    didUnregister = true;

    const activeTabs = readActiveTabs();
    delete activeTabs[tabId];
    const remainingTabs = writeActiveTabs(activeTabs);

    if (
      Object.keys(remainingTabs).length === 0 &&
      getSharedAuthState()?.status === 'authenticated'
    ) {
      writePendingLastTabClose({
        tabId,
        timestamp: Date.now(),
      });
    }
  };

  const handlePageHide = (event) => {
    if (event?.persisted) {
      return;
    }
    unregisterTab();
  };

  const handlePageShow = (event) => {
    if (!event?.persisted) {
      return;
    }

    didUnregister = false;
    cancelPendingLastTabCloseForTab(tabId);
    touchTab();
    refreshSessionIfNeeded();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    cancelPendingLastTabCloseForTab(tabId);
    touchTab();
    refreshSessionIfNeeded();
  };

  const handleWindowFocus = () => {
    cancelPendingLastTabCloseForTab(tabId);
    touchTab();
    refreshSessionIfNeeded();
  };

  const runKeepaliveRefresh = () => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    if (hasAuthenticatedSession() || shouldAttemptSessionRefresh()) {
      refreshSessionIfNeeded();
    }
  };

  touchTab();
  const heartbeatId = window.setInterval(touchTab, AUTH_TAB_HEARTBEAT_MS);
  const keepaliveId = window.setInterval(runKeepaliveRefresh, AUTH_SESSION_KEEPALIVE_MS);

  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('pageshow', handlePageShow);
  window.addEventListener('beforeunload', unregisterTab);
  window.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleWindowFocus);

  return () => {
    window.clearInterval(heartbeatId);
    window.clearInterval(keepaliveId);
    window.removeEventListener('pagehide', handlePageHide);
    window.removeEventListener('pageshow', handlePageShow);
    window.removeEventListener('beforeunload', unregisterTab);
    window.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', handleWindowFocus);
    unregisterTab();
  };
};

export const publishAuthEvent = (type, payload = {}) => {
  const authEvent = createAuthEvent(type, payload);
  const currentUser = getCurrentAuthEventUser(payload);

  if (type === AUTH_EVENT_TYPES.logout) {
    markSharedLoggedOut(payload.reason || 'logout');
  } else if (
    (type === AUTH_EVENT_TYPES.login ||
      type === AUTH_EVENT_TYPES.sessionRefreshed) &&
    currentUser
  ) {
    markSharedAuthenticated(currentUser, {
      sourceEvent: type,
    });
  }

  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.postMessage(authEvent);
    channel.close();
  }

  try {
    localStorage.setItem(AUTH_EVENT_STORAGE_KEY, JSON.stringify(authEvent));
  } catch (error) {
    console.error('Error publishing auth event:', error);
  }

  return authEvent;
};

export const subscribeToAuthEvents = (listener) => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  let channel = null;

  const handleEvent = (authEvent) => {
    if (!authEvent?.type) return;
    listener(authEvent);
  };

  const handleStorageEvent = (event) => {
    if (event.key !== AUTH_EVENT_STORAGE_KEY || !event.newValue) return;

    try {
      handleEvent(JSON.parse(event.newValue));
    } catch (error) {
      console.error('Error reading auth storage event:', error);
    }
  };

  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.onmessage = (event) => handleEvent(event.data);
  }

  window.addEventListener('storage', handleStorageEvent);

  return () => {
    window.removeEventListener('storage', handleStorageEvent);
    if (channel) {
      channel.close();
    }
  };
};
